import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tag } from '@/components/ui/tag';
import PaginationControls from '@/components/shared/PaginationControls';
import { Utensils, ChevronDown, ChevronUp, Loader2, Clock, Users, UtensilsCrossed } from 'lucide-react';
import { DEFAULT_RECIPE_IMAGE } from '@/constants/media';
import { formatRecipeSource } from '@/lib/utils';
import { RecipeAllergenCard } from '@/components/recipes/RecipeAllergenCard';

type MealPlanMealSectionProps = {
  dayNumber: number;
  mealType: string;
  mealData: any;
  isExpanded: boolean;
  loadingPagination: boolean;
  loadingSelections: Set<string>;
  onToggle: (mealKey: string) => void;
  onSelectRecipe: (day: number, mealType: string, recipeId: string) => void;
  onViewRecipe: (recipeId: string, recipeName: string, day: number, mealName: string, source: string) => void;
  onPageChange: (day: number, mealType: string, newPage: number) => void;
};

const MealPlanMealSection: React.FC<MealPlanMealSectionProps> = ({
  dayNumber,
  mealType,
  mealData,
  isExpanded,
  loadingPagination,
  loadingSelections,
  onToggle,
  onSelectRecipe,
  onViewRecipe,
  onPageChange,
}) => {
  const recipes = mealData.recipes || [];
  const mealKey = `${dayNumber}-${mealType}`;
  const pagination = mealData.pagination;
  const currentPage = pagination?.currentPage || 1;

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6">
        <div
          className="flex items-center justify-between cursor-pointer hover:bg-gray-50 -m-6 p-6 rounded-lg transition-colors"
          onClick={() => onToggle(mealKey)}
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center">
              <Utensils className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold capitalize">{mealType}</h3>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {!recipes.some((recipe) => recipe.isSelected) && (
              <Tag variant="danger" size="sm">
                Required
              </Tag>
            )}
            <Button variant="ghost" size="sm">
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-4 pt-4 border-t">
            {loadingPagination ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600 mr-3" />
                <span className="text-gray-600">Loading recipes...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recipes.map((recipe: any) => (
                  <Card
                    key={recipe.id}
                    className={`overflow-hidden hover:shadow-lg transition-shadow ${
                      recipe.isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                    }`}
                  >
                    <div className="relative h-40">
                      <img
                        src={recipe.image || DEFAULT_RECIPE_IMAGE}
                        alt={recipe.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.onerror = null;
                          target.src = DEFAULT_RECIPE_IMAGE;
                        }}
                      />
                      <div className="absolute top-2 right-2 flex gap-1">
                        {recipe.readyInMinutes && (
                          <Tag variant="neutral" size="sm" startIcon={<Clock className="h-3 w-3" />} className="bg-white/90 text-gray-700">
                            {recipe.readyInMinutes}m
                          </Tag>
                        )}
                        <Tag variant="neutral" size="sm" startIcon={<Users className="h-3 w-3" />} className="bg-white/90 text-gray-700">
                          {recipe.nutritionServings ?? recipe.servings} 
                        </Tag>
                        {recipe.source && (
                          <Tag variant="neutral" size="sm" className="bg-white/90 text-gray-700">
                            {formatRecipeSource(recipe.source)}
                          </Tag>
                        )}
                      </div>
                      {recipe.isSelected && (
                        <div className="absolute top-2 left-2">
                          <Tag variant="selected" size="sm" className="border-0 bg-blue-500 text-white">✓ Selected</Tag>
                        </div>
                      )}
                    </div>

                    <CardContent className="p-4 space-y-3">
                      <h4 className="font-semibold text-base line-clamp-2 leading-tight min-h-[2.5rem]">
                        {recipe.title}
                      </h4>

                      <div className="grid grid-cols-5 gap-1.5 text-xs">
                        <div className="text-center bg-orange-50 border border-orange-200 rounded py-1.5">
                          <div className="font-bold text-orange-700">{recipe.calories}</div>
                          <div className="text-xs text-gray-600">Cal</div>
                        </div>
                        <div className="text-center bg-blue-50 border border-blue-200 rounded py-1.5">
                          <div className="font-bold text-blue-700">{recipe.protein}g</div>
                          <div className="text-xs text-gray-600">Pro</div>
                        </div>
                        <div className="text-center bg-green-50 border border-green-200 rounded py-1.5">
                          <div className="font-bold text-green-700">{recipe.carbs}g</div>
                          <div className="text-xs text-gray-600">Carb</div>
                        </div>
                        <div className="text-center bg-amber-50 border border-amber-200 rounded py-1.5">
                          <div className="font-bold text-amber-700">{recipe.fat}g</div>
                          <div className="text-xs text-gray-600">Fat</div>
                        </div>
                        <div className="text-center bg-teal-50 border border-teal-200 rounded py-1.5">
                          <div className="font-bold text-teal-700">{recipe.fiber}g</div>
                          <div className="text-xs text-gray-600">Fib</div>
                        </div>
                      </div>

                      <RecipeAllergenCard
                        allergens={recipe.allergens}
                        compact
                        maxVisible={3}
                      />

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={recipe.isSelected ? 'default' : 'outline'}
                          className="flex-1 text-xs h-8"
                          onClick={() => onSelectRecipe(dayNumber, mealType, recipe.id)}
                          disabled={loadingSelections.has(`${dayNumber}-${mealType}-${recipe.id}`)}
                        >
                          {loadingSelections.has(`${dayNumber}-${mealType}-${recipe.id}`) ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2" />
                              Processing...
                            </>
                          ) : (
                            recipe.isSelected ? 'Selected' : 'Select'
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs h-8"
                          onClick={() => onViewRecipe(recipe.id, recipe.title, dayNumber, mealType, recipe.source)}
                        >
                          View
                        </Button>
                        {recipe.sourceUrl && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => window.open(recipe.sourceUrl, '_blank')}
                          >
                            <UtensilsCrossed className="h-3 w-3 mr-1" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {pagination && pagination.totalPages > 1 && !loadingPagination && (
              <div className="mt-6 pt-4 border-t">
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={pagination.totalPages}
                  onPageChange={(page) => onPageChange(dayNumber, mealType, page)}
                  className="justify-center"
                  maxVisiblePages={pagination.totalPages}
                  summaryText={`Page ${currentPage} of ${pagination.totalPages}`}
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MealPlanMealSection;
